// Capa FINA sobre atlasBackendFetch para `/v1/verificacion/*`. Sin lógica: interpreta el
// sobre `{ response }` y convierte el 402 en `GenerateAdError('billing')` para que el
// camino de las generaciones delegadas (InterfazV2) abra el modal de plan sin código
// nuevo (ADR-2026-09-07-verificacion-de-calidad-en-la-columna). El resto de los errores
// viaja como `Error` con el `message` del backend, que ya viene en castellano.
import { atlasBackendFetch } from '../../../lib/atlasBackend';
import { GenerateAdError, mapErrorTypeToMessage } from '../../../lib/generateAdRequest';
import type { ConfiguracionGuardada, ItemVerificacion, RegeneracionLanzada } from './tipos';

const BASE = '/v1/verificacion';

interface Sobre {
  response?: 'success' | 'error';
  error_type?: string;
  message?: string;
}

async function leer<T>(res: Response, que: string): Promise<T> {
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  const sobre = (json ?? {}) as Sobre & Record<string, unknown>;
  if (res.status === 402) {
    throw new GenerateAdError('billing', sobre.message ?? mapErrorTypeToMessage('billing'), null);
  }
  if (sobre.response === 'error' || !(res.ok || res.status === 202)) {
    throw new Error(sobre.message ?? `${que}: HTTP ${res.status}`);
  }
  return sobre as unknown as T;
}

export async function listarVerificacion(ids: string[]): Promise<ItemVerificacion[]> {
  const res = await atlasBackendFetch(`${BASE}/listar`, { imagen_ids: ids });
  const j = await leer<{ items: ItemVerificacion[] }>(res, 'listar');
  // `productos` se agregó el 2026-09-08: un backend anterior al deploy no lo manda y el
  // popover haría `.length` sobre undefined.
  return (j.items ?? []).map((it) => ({
    ...it,
    productos: it.productos ?? [],
    modo_utilizado: it.modo_utilizado ?? null,
    // `modalidad` se agregó el 2026-09-13: un backend anterior al deploy no la manda, y
    // todo lo que analizaba hasta entonces era contra el producto.
    modalidad: it.modalidad ?? (it.analizable ? 'producto' : null),
    creditos: typeof it.creditos === 'number' ? it.creditos : null,
  }));
}

export async function analizarImagen(id: string): Promise<{ run_id: string }> {
  const res = await atlasBackendFetch(`${BASE}/analizar`, { imagen_id: id });
  return leer<{ run_id: string }>(res, 'analizar');
}

export async function regenerarImagen(id: string, executionId: string): Promise<RegeneracionLanzada> {
  const res = await atlasBackendFetch(`${BASE}/regenerar`, { imagen_id: id, execution_id: executionId });
  const j = await leer<RegeneracionLanzada>(res, 'regenerar');
  return {
    execution_id: j.execution_id,
    cantidad: j.cantidad ?? 1,
    creditos: Number(j.creditos ?? 1),
    formato: j.formato ?? null,
  };
}

/**
 * El pedido guardado de una imagen, para precargar el modo antes de regenerar
 * (ADR-2026-09-09-ajustar-antes-de-regenerar). Viene en el vocabulario del backend; lo
 * traduce `configurarRegeneracion.ts`.
 */
export async function configuracionDeImagen(id: string): Promise<ConfiguracionGuardada> {
  const res = await atlasBackendFetch(`${BASE}/configuracion`, { imagen_id: id });
  const j = await leer<ConfiguracionGuardada>(res, 'configuracion');
  return { pedido: j.pedido ?? {}, modo_utilizado: j.modo_utilizado ?? null };
}
